import type { ViewerShell } from '../../../shell';
import { confirmDialog } from '../../../ui/dialogs';
import { RisError } from '../../client';
import type { AssistantApi } from '../api';
import { conversationStudy } from '../context';
import { askErrorText } from '../panel';
import { cleanParts, clipboardText, isToolDisabled, partsText, type ReportPart, SEND_LABEL, toolErrorText } from './logic';
import { SECTION_LABELS } from './shapes';
import { markToolOff, reportSendMode } from './switches';

/**
 * "Enviar al informe" (contract v3 §2.1): every model-written text a tool offers for the report goes
 * through here. The confirmation shows ALL of it, section by section, before anything leaves the
 * viewer. With the tool on and `ris:reports:write` on the ticket it is appended to the reader's own
 * draft (ris records it as assisted, so the signer attests it); otherwise it is copied to the
 * clipboard as before ("Copiar al informe").
 */

export const SEND_TEXT = {
  sendIntro: 'Revise el texto sugerido antes de enviarlo. Se añadirá exactamente esto a su borrador del informe, marcado como texto asistido:',
  copyIntro: 'Revise el texto sugerido antes de copiarlo. Se copiará exactamente esto:',
  sendConfirm: 'Enviar',
  copyConfirm: 'Copiar',
  cancel: 'Cancelar',
  sent: (sections: string) => `Texto enviado al borrador del informe (${sections}).`,
  partial: (done: number, all: number) => `Se enviaron ${done} de ${all} secciones al borrador del informe.`,
  copied: 'Texto copiado para el informe.',
  copyFailed: 'No se pudo copiar el texto.',
  conflict: 'El borrador cambió mientras se enviaba el texto; intente de nuevo.',
  failed: 'No fue posible enviar el texto al informe.',
  disabledNowCopy: 'Enviar al informe se desactivó para la organización; la próxima vez el texto se copiará.',
} as const;

/** The button label for the current session ("Enviar al informe" or "Copiar al informe"). */
export function sendLabel(): string {
  return SEND_LABEL[reportSendMode()];
}

function sendErrorText(error: unknown): string {
  const known = toolErrorText(error);
  if (known) return known;
  if (error instanceof RisError && error.status === 409) return SEND_TEXT.conflict;
  return error instanceof RisError ? askErrorText(error) : SEND_TEXT.failed;
}

async function copy(shell: ViewerShell, text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    shell.toast(SEND_TEXT.copied);
    return true;
  } catch (error) {
    console.warn('[viewer] clipboard write failed', error);
    shell.toast(SEND_TEXT.copyFailed, 'error');
    return false;
  }
}

async function insertAll(shell: ViewerShell, api: AssistantApi, study: string, parts: ReportPart[]): Promise<boolean> {
  let done = 0;
  try {
    for (const part of parts) {
      await api.insertIntoReport(study, part.section, part.text);
      done += 1;
    }
  } catch (error) {
    console.warn('[viewer] send to report failed', error);
    if (isToolDisabled(error)) markToolOff('send_to_report');
    const text = isToolDisabled(error) ? `${sendErrorText(error)} ${SEND_TEXT.disabledNowCopy}` : sendErrorText(error);
    shell.toast(done > 0 ? `${SEND_TEXT.partial(done, parts.length)} ${text}` : text, 'error');
    return false;
  }
  shell.toast(SEND_TEXT.sent(parts.map((p) => SECTION_LABELS[p.section].toLowerCase()).join(', ')));
  return true;
}

/**
 * Confirms, then sends (or copies) the parts. Resolves true when the text went where the dialog said.
 * `study`: the study the text was written for (default: the conversation's).
 */
export async function sendToReport(shell: ViewerShell, api: AssistantApi, given: readonly ReportPart[], study = conversationStudy(shell)): Promise<boolean> {
  const parts = cleanParts(given);
  if (parts.length === 0) return false;
  const mode = reportSendMode();
  const shown = mode === 'send' ? partsText(parts) : clipboardText(parts);
  const confirmed = await confirmDialog(shell, {
    title: SEND_LABEL[mode],
    text: `${mode === 'send' ? SEND_TEXT.sendIntro : SEND_TEXT.copyIntro}\n\n${shown}`,
    confirmLabel: mode === 'send' ? SEND_TEXT.sendConfirm : SEND_TEXT.copyConfirm,
    cancelLabel: SEND_TEXT.cancel,
    className: 'assistant-insert-dialog assistant-send-dialog',
  });
  if (!confirmed) return false;
  return mode === 'send' ? insertAll(shell, api, study, parts) : copy(shell, shown);
}
